import { useTranslation } from 'react-i18next';
import { AnimatedCurrency } from '@hub/components/animated-currency';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Typography, useBreakpoints } from '@monorepo/nova-ui';
import { Account, AccountBalance, Program } from '@monorepo/types';
import {
  DEFAULT_VALUES,
  OBJECTIVE_ICONS,
  PROFITABLE_DAYS_CONSTANTS,
} from '../constants/objective-constants';
import { ObjectiveData } from '../types';
import { getMaxLossValue } from '../utils/objective-calculations';
import { getMainTextStyle } from '../utils/styles';

interface MaxLossObjectiveProps {
  account?: Account;
  accountBalance?: AccountBalance;
  program: Program | undefined | null;
}

export const MaxLossObjective = ({
  program,
}: MaxLossObjectiveProps): ObjectiveData => {
  const { t } = useTranslation('dashboardHeader');
  const theme = useTheme();
  const isMobile = useBreakpoints();

  const currentValue = program ? getMaxLossValue(program) : 0;

  const textColor =
    getMainTextStyle(theme)?.color || theme.palette.text.primary;

  const tooltipContent = !isMobile ? (
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.text.opposite,
        lineHeight: theme.typography.body2.lineHeight,
      }}
    >
      {t('objectives.maxLoss.tooltip')}
    </Typography>
  ) : undefined;

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {t('objectives.maxLoss.label')}
      </Typography>
      {!isMobile && tooltipContent && (
        <Tooltip title={tooltipContent}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{
                color: theme.palette.primary.main,
              }}
            >
              <SystemIcon
                iconName={OBJECTIVE_ICONS.INFO}
                size={PROFITABLE_DAYS_CONSTANTS.INFO_ICON_SIZE}
                sx={{
                  opacity: 0.6,
                }}
              />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return {
    icon: (
      <SystemIcon
        iconName="Speedometer"
        size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE}
      />
    ),
    primaryText: primaryLabel,
    secondaryText: (
      <Box>
        <AnimatedCurrency
          value={currentValue}
          fontSize={20}
          textColor={textColor}
          compact={true}
          currencySymbol="$"
          showAnimation={true}
          fontStyle="normal"
          showDecimals={false}
          letterSpacing="0.03px"
        />
      </Box>
    ),
  };
};
